import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { filter, finalize, map, shareReplay, tap } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { AuthService } from '../../auth/services/auth.service';
import { InstanceConfigService } from '../../../core/services/instance-config.service';
import { ResumenCliente } from './tenants.service';

/** La cuota del mes en curso de un cliente, como la muestra «Cobranza». */
export interface CuotaDelMes {
  /** SIN_GENERAR, PENDIENTE, COMPROBANTE_ENVIADO, ATRASADO, PAGADO o ANULADO. */
  estado: string;
  montoUf: number | null;
  /** Con IVA. */
  montoClp: number | null;
  vence: string | null;
  diasAtraso: number;
}

/**
 * Lo que el resumen trae además de lo de {@link ResumenCliente} (29-oct). Todo opcional: un backend sin desplegar no
 * lo manda, y la pantalla tiene que mostrar «—» en vez de romperse o de inventar un cero.
 */
export interface ClientePanel extends ResumenCliente {
  rut?: string | null;
  diasGracia?: number | null;
  /** Nulo: no se pudo leer. 0 o menos: sin tope. */
  maxUsuarios?: number | null;
  maxEquipos?: number | null;
  /** La base existe pero no tiene empresa: el alta quedó a medias. Nulo si la base no respondió. */
  altaPendiente?: boolean | null;
  registradoEn?: string | null;
  cobroConfigurado?: boolean;
  /** Lo que paga al mes, con IVA. */
  cuotaUf?: number | null;
  cuotaClp?: number | null;
  /** La UF con que se calculó la cuota. */
  valorUf?: number | null;
  cuotaMes?: CuotaDelMes | null;
  bloqueadas?: string[];
  enConstruccion?: string[];
}

/** Cada cuánto, como mucho, se vuelve a preguntar al navegar. El backend cachea el resumen un minuto. */
const ESPERA_MINIMA_MS = 60_000;

/**
 * El resumen de clientes del panel de plataforma, una sola vez para toda la cuenta.
 *
 * <p>Lo leen la pantalla Clientes y los contadores del menú —altas a medias y cobros atrasados—, que tienen que decir
 * lo mismo: con una consulta por lado, el menú podía marcar dos atrasados mientras la lista mostraba uno.
 *
 * <p>Se refresca al navegar, con tope de un minuto, igual que los contadores del taller. Solo pregunta en la cuenta de
 * la plataforma y con SUPER_ADMIN: en la cuenta de un cliente la ruta ni existe.
 */
@Injectable({ providedIn: 'root' })
export class PanelResumenService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private instanceConfig = inject(InstanceConfigService);
  private router = inject(Router);

  private readonly datos = signal<ClientePanel[] | null>(null);
  private readonly enCurso = signal(false);
  private readonly fallo = signal(false);

  /** Todos los registrados menos nuestra cuenta, incluidas las altas a medias. Nulo hasta la primera respuesta. */
  readonly clientes = this.datos.asReadonly();
  /**
   * Todavía sin nada que mostrar ni error que contar: el esqueleto. Un refresco con datos a la vista no vuelve a él.
   * No depende de que haya una consulta en curso: mientras llega la configuración de la cuenta no hay ninguna, y sin
   * esto la pantalla alcanzaba a decir «todavía no hay clientes».
   */
  readonly cargando = computed(() => this.datos() === null && !this.fallo());
  /** La última consulta falló y no hay datos anteriores. */
  readonly error = computed(() => this.fallo() && this.datos() === null);

  /** Altas registradas cuya base no tiene empresa todavía. */
  readonly altasPendientes = computed(() => (this.datos() ?? []).filter(c => c.altaPendiente === true));
  /** Cuotas del mes que vencieron sin pagarse. */
  readonly cobrosAtrasados = computed(() =>
    (this.datos() ?? []).filter(c => c.cuotaMes?.estado === 'ATRASADO').length);

  private ultimaConsulta = 0;
  private enVuelo: Observable<ClientePanel[]> | null = null;

  constructor() {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.refrescar({ silencioso: true }));

    // La configuración de instancia llega después del arranque: sin esto los contadores del menú quedan vacíos
    // hasta la primera navegación. `untracked`: el efecto depende solo de la cuenta; si dependiera del estado de la
    // consulta, cada error dispararía otra consulta.
    effect(() => {
      if (this.instanceConfig.esPanelPlataforma()) untracked(() => this.refrescar({ silencioso: true }));
    });

    // El siguiente que entre en este navegador no hereda los números del anterior.
    this.auth.isAuthenticated$.subscribe(autenticado => { if (!autenticado) this.limpiar(); });
  }

  /**
   * Vuelve a preguntar, con tope de tiempo salvo que se fuerce.
   *
   * @param opciones.forzar     sin esperar el minuto: «Reintentar» y la primera carga de la pantalla. La pide la
   *                            pantalla de Clientes, así que no espera a que la configuración diga que esta es la
   *                            cuenta de la plataforma: si no lo es, el servidor responde que no.
   * @param opciones.silencioso sin aviso ni barra de carga: los refrescos de fondo al navegar. Un contador que no
   *                            cargó no merece un aviso de error; la pantalla, sí.
   */
  refrescar(opciones: { forzar?: boolean; silencioso?: boolean } = {}): void {
    if (!this.puedeConsultar(!!opciones.forzar)) {
      // La pantalla no puede quedarse en el esqueleto para siempre: sin permiso es un error, no una carga.
      if (opciones.forzar) this.fallo.set(true);
      return;
    }
    if (this.enCurso()) return;
    const ahora = Date.now();
    if (!opciones.forzar && ahora - this.ultimaConsulta < ESPERA_MINIMA_MS) return;

    this.ultimaConsulta = ahora;
    this.enCurso.set(true);
    // «Reintentar» vuelve al esqueleto mientras pregunta, en vez de dejar el error a la vista.
    if (opciones.forzar) this.fallo.set(false);
    const headers = opciones.silencioso
      ? new HttpHeaders({ 'X-Skip-Error-Toast': 'true', 'X-Skip-Loading': 'true' })
      : undefined;

    this.consulta(headers).subscribe({
      next: (lista) => {
        this.enCurso.set(false);
        this.fallo.set(false);
        this.datos.set(lista);
      },
      error: () => {
        this.enCurso.set(false);
        this.fallo.set(true);
        // Para que «Reintentar» no espere el minuto.
        this.ultimaConsulta = 0;
      },
    });
  }

  /**
   * La lista para la navegación entre registros de la ficha de un cliente: la que ya está, o una consulta silenciosa
   * (sin aviso, sin barra de carga y sin los reintentos del arranque en frío) si todavía no llegó, por ejemplo tras un
   * F5 en la ficha. La ficha muestra su propia carga y su propio «Reintentar».
   */
  lista(): Observable<ClientePanel[]> {
    const ya = this.datos();
    if (ya) return of(ya);
    const headers = new HttpHeaders({ 'X-Skip-Error-Toast': 'true', 'X-Skip-Loading': 'true', 'X-Skip-Retry': 'true' });
    return this.consulta(headers).pipe(tap(lista => { if (this.datos() === null) this.datos.set(lista); }));
  }

  /**
   * La consulta al registro central, una a la vez: el resumen recorre la base de cada cliente, así que si el menú y la
   * navegación de la ficha lo piden juntos (un F5 en la ficha), el segundo se suma al que ya va en camino.
   */
  private consulta(headers?: HttpHeaders): Observable<ClientePanel[]> {
    this.enVuelo ??= this.http.get<unknown>(`${environment.apiUrl}/admin/tenants/resumen`, { headers }).pipe(
      // El endpoint devuelve el arreglo crudo; cualquier otra forma no se pinta.
      map(r => (Array.isArray(r) ? r as ClientePanel[] : [])),
      finalize(() => { this.enVuelo = null; }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.enVuelo;
  }

  limpiar(): void {
    this.datos.set(null);
    this.fallo.set(false);
    this.ultimaConsulta = 0;
  }

  /** Los refrescos de fondo, solo en la cuenta de la plataforma: en la de un cliente la ruta ni existe. */
  private puedeConsultar(desdeLaPantalla: boolean): boolean {
    return (desdeLaPantalla || this.instanceConfig.esPanelPlataforma())
      && this.auth.isAuthenticated()
      && this.auth.hasAnyRole(['ROLE_SUPER_ADMIN']);
  }
}
